import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as queryString from 'query-string';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import Button from '../../components/Button';
import ErrorMessage, { useError } from '../../components/ErrorMessage';
import { login } from '../../lib/authentication';
import * as types from '../../types';
import styles from './Login.module.scss';
import { UserFacingError } from '../../lib/errors';
import Loading from '../../components/Loading';

function ForgotPasswordLink() {
  const { t } = useTranslation();
  return <Link to={AppPaths.ForgotPassword}>{t('forgot_your_password')}</Link>;
}

interface LoginLocationState {
  /**
   * Should be true in the situation where the user tried to make a purchase with Apple Pay while not logged in,
   * but we've determined the email address used to make the purchase is already registered.
   */
  applePayError?: boolean;
  /**
   * Should be true in the situation where the user tried to check out with Stripe Elements while not logged in,
   * but we've determined the email address used is already registered.
   */
  stripeCheckoutError?: boolean;
  /**
   * Should be true in the situation where the user tried to create a new account and we encountered an error
   * when creating their billing account which happens as a separate mutation after the user creation.
   */
  billingAccountCreationError?: boolean;
}

export default function Login() {
  const { t } = useTranslation();
  const location = useLocation<LoginLocationState>();
  const history = useHistory();
  const { error, errorID, setError } = useError();
  const [submitting, setSubmitting] = React.useState(false);
  const loggedIn = useSelector((state: types.AppState) => !!state.session);

  const queryParams = queryString.parse(location.search);
  const returnTo = queryParams.returnTo ? (queryParams.returnTo as string) : undefined;
  const applePayError = !!location.state?.applePayError;
  const stripeCheckoutError = !!location.state?.stripeCheckoutError;
  const billingAccountCreationError = !!location.state?.billingAccountCreationError;

  const handleLogin = useCallback(
    (event: any) => {
      event.preventDefault();
      if (submitting || loggedIn) {
        return;
      }

      setSubmitting(true);

      const email = event.target.email.value.trim();
      const password = event.target.password.value.trim();

      login(email, password)
        .then(() => {
          history.replace(returnTo ?? AppPaths.Home);
        })
        .catch((err) => {
          setSubmitting(false);

          if (err instanceof UserFacingError) {
            setError(err.message);
            return;
          }

          setError(t('an_error_occurred_while_logging_in_please_try_again_or_contact_support'));
        });
    },
    [history, loggedIn, returnTo, setError, submitting, t],
  );

  if (loggedIn) {
    // Only redirect if we landed on this page while already logged in.
    // If we've logged in after submitting the login form, don't worry about redirecting using a component. Instead,
    // the success callback on the login promise will handle the redirect. This seems to resolve an issue where the
    // components on the returnTo page were rendered twice and this can cause a problem with parallel Recurly pricing
    // calls on the cart page if the user has a single-use coupon attached to the cart.
    return submitting ? <Loading /> : <Redirect to={returnTo ?? AppPaths.Home} />;
  }

  return (
    <div className={styles.container}>
      <h2>{t('login')}</h2>
      {!error && applePayError && (
        <div>
          <span className={styles.signUpError}>
            {t('it_looks_like_you_already_have_a_fi_account_please_log_in_and_try_your_purchase_again')}
          </span>
          <ForgotPasswordLink />
        </div>
      )}
      {!error && stripeCheckoutError && (
        <div>
          <span className={styles.signUpError}>
            {t('an_account_with_this_email_already_exists_please_log_in_to_complete_your_purchase')}
          </span>
          <ForgotPasswordLink />
        </div>
      )}
      {!error && billingAccountCreationError && (
        <div className={styles.signUpError}>
          {/* This isn't technically true, but by having them log in again it will make another attempt at billing account creation */}
          {t('we_have_created_your_account_successfully_but_had_trouble_logging_you_in_please_try_logging_in_again')}
        </div>
      )}
      <form onSubmit={handleLogin} className={styles.loginForm}>
        <div className={styles.formSection}>
          <input type="text" name="email" placeholder={t('email')} />
          <input type="password" name="password" placeholder={t('password')} />
        </div>
        <Button className={styles.formActions} type="submit" disabled={submitting}>
          {t('login')}
        </Button>
        {error && <ErrorMessage errors={[error]} errorID={errorID} />}
      </form>
      <div className={styles.actionLinks}>
        <Link to={returnTo ?? '/'}>{t('nevermind_take_me_back')}</Link>
        <ForgotPasswordLink />
      </div>
    </div>
  );
}
